// useAnchorTermSheet — client-side anchoring of a term-sheet digest to the TermSheetRegistry contract from the user's own wallet (viem, EIP-1193).
import { useCallback, useState } from "react";
import { stringToHex } from "viem";
import api, { apiErr } from "@/lib/api";
import { REGISTRY_ABI, REGISTRY_BYTECODE, hasWallet, switchChain, txUrl, walletClients } from "@/lib/web3Registry";

const friendly = (e) => {
  const m = e?.shortMessage || e?.message || String(e);
  if (/AlreadyAnchored/.test(m)) return "This digest is already anchored on the registry.";
  if (/User rejected|denied/i.test(m)) return "Signature rejected in the wallet.";
  if (/insufficient funds/i.test(m)) return "Wallet has no gas on this chain — use the faucet linked below.";
  return m.length > 220 ? m.slice(0, 220) + "…" : m;
};

export const useAnchorTermSheet = (registry) => {
  const [state, setState] = useState({ status: "idle", txHash: null, error: null, account: null, chainId: null, receipt: null });
  const reset = useCallback(() => setState({ status: "idle", txHash: null, error: null, account: null, chainId: null, receipt: null }), []);

  const connect = useCallback(async (wantChain) => {
    setState((s) => ({ ...s, status: "connecting", error: null }));
    let c = await walletClients();
    if (wantChain && c.chainId !== Number(wantChain)) { await switchChain(Number(wantChain)); c = await walletClients(); }
    setState((s) => ({ ...s, account: c.account, chainId: c.chainId }));
    return c;
  }, []);

  const anchor = useCallback(async ({ evaluationId, digest, tier, afadClass, version }) => {
    try {
      const { wallet, pub, account, chainId } = await connect(registry.chain_id);
      setState((s) => ({ ...s, status: "signing" }));
      const tierNo = Number(String(tier).replace(/\D/g, "").slice(0, 1)) || 4;
      const hash = await wallet.writeContract({ address: registry.contract, abi: REGISTRY_ABI, functionName: "anchor", args: [digest, tierNo, stringToHex(afadClass || "E", { size: 1 }), evaluationId, Number(version) || 1], account });
      setState((s) => ({ ...s, status: "pending", txHash: hash }));
      await pub.waitForTransactionReceipt({ hash });
      const { data } = await api.post(`/housing/evaluations/${evaluationId}/anchor-receipt`, { tx_hash: hash, chain_id: chainId, contract: registry.contract, issuer: account });
      setState((s) => ({ ...s, status: data.onchain?.confirmed ? "confirmed" : "mismatch", receipt: data.onchain, txUrl: data.onchain?.tx_url || txUrl(chainId, hash) }));
      return data;
    } catch (e) {
      const msg = e?.response ? apiErr(e.response.data?.detail) : friendly(e);
      setState((s) => ({ ...s, status: /already anchored/i.test(msg) ? "already" : "error", error: msg }));
      return null;
    }
  }, [connect, registry]);

  const deploy = useCallback(async (chainId) => {
    try {
      const { wallet, pub, account, chainId: cid } = await connect(chainId || null);
      setState((s) => ({ ...s, status: "signing" }));
      const hash = await wallet.deployContract({ abi: REGISTRY_ABI, bytecode: REGISTRY_BYTECODE, account, args: [] });
      setState((s) => ({ ...s, status: "pending", txHash: hash }));
      const rc = await pub.waitForTransactionReceipt({ hash });
      const { data } = await api.put("/web3/registry", { chain_id: cid, contract: rc.contractAddress, deploy_tx: hash });
      setState((s) => ({ ...s, status: "deployed", receipt: data, txUrl: txUrl(cid, hash) }));
      return data;
    } catch (e) {
      setState((s) => ({ ...s, status: "error", error: e?.response ? apiErr(e.response.data?.detail) : friendly(e) }));
      return null;
    }
  }, [connect]);

  return { ...state, anchor, deploy, reset, walletAvailable: hasWallet(), ready: hasWallet() && !!registry?.configured };
};
